import StaffProfileFields from '../StaffProfileFields';
import type { StaffProfile } from '../StaffProfiles';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FormField from '../ui/FormField';
import Button from '../ui/Button';
import { useSeedEffect, type SeedKey } from '../../hooks/useSeedEffect';
import { validate, required, email } from '../../utils/validation';
import PasswordInput from '../PasswordInput';
import '../../App.css';

interface IMember {
  _id: string;
  name: string;
}

interface IUserFormData {
  staffProfile?: StaffProfile;
  name: string;
  email: string;
  phoneNumber: string;
  password?: string;
  roleId?: string;
}

interface IUserFormProps {
  initialData?: {
    staffProfile?: StaffProfile;
    name: string;
    email: string;
    phoneNumber: string;
    roleId?: string;
  };
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  onSubmit: (data: IUserFormData) => Promise<void>;
  loading: boolean;
  submitButtonText: string;
  isEditMode?: boolean;
  assignedMembers?: IMember[];
  roles?: Array<{ _id: string; name: string }>;
}

const UserForm: React.FC<IUserFormProps> = ({
  initialData,
  seedKey,
  onSubmit,
  loading,
  submitButtonText,
  isEditMode = false,
  assignedMembers = [],
  roles = [],
}) => {
  const { t } = useTranslation();
  const [staffProfile, setStaffProfile] = useState<StaffProfile>({ aboutMe: '', photoUrl: null });
  const [photoBusy, setPhotoBusy] = useState(false);
  const [name, setName] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [roleId, setRoleId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Seeded once per record (seedKey), else per initialData content.
  useSeedEffect(initialData, seedKey, (seed) => {
    if (!seed) return;
    setStaffProfile(seed.staffProfile ?? { aboutMe: '', photoUrl: null });
    setName(seed.name);
    setEmailValue(seed.email);
    setPhoneNumber(seed.phoneNumber);
    if (seed.roleId) setRoleId(seed.roleId);
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameError = validate(name, [required(t('validation.nameRequired'))]);
    if (nameError) newErrors.name = nameError;

    const emailError = validate(emailValue, [
      required(t('validation.emailRequired')),
      email(t('validation.emailInvalid')),
    ]);
    if (emailError) newErrors.email = emailError;

    if (!isEditMode) {
      const passwordError = validate(password, [required(t('validation.passwordRequired'))]);
      if (passwordError) newErrors.password = passwordError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    if (photoBusy || !validateForm()) return;

    const data: IUserFormData = { name, email: emailValue, phoneNumber, ...(isEditMode ? { staffProfile } : {}) };
    if (password) {
      data.password = password;
    }
    if (roleId) {
      data.roleId = roleId;
    }
    onSubmit(data);
  };

  return (
    <form onSubmit={submitHandler} className="form-container">
      <FormField
        htmlFor="name"
        label={t('users.name')}
        error={errors.name}
        required
      >
        <input
          type="text"
          id="name"
          placeholder={t('users.enterName')}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
          }}
        />
      </FormField>
      <FormField
        htmlFor="email"
        label={t('common.email')}
        error={errors.email}
        required
      >
        <input
          type="email"
          id="email"
          placeholder={t('users.enterEmail')}
          value={emailValue}
          onChange={(e) => {
            setEmailValue(e.target.value);
            if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
          }}
        />
      </FormField>
      <FormField
        htmlFor="phoneNumber"
        label={t('common.phoneNumber')}
      >
        <input
          type="tel"
          id="phoneNumber"
          placeholder={t('users.enterPhoneNumber')}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
      </FormField>
      <FormField
        htmlFor="password"
        label={t('common.password')}
        error={errors.password}
        required={!isEditMode}
      >
        <PasswordInput
          id="password"
          placeholder={isEditMode ? t('users.leavePasswordBlank') : t('users.enterPassword')}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
          }}
        />
      </FormField>
      {roles.length > 0 && (
        <FormField
          htmlFor="roleId"
          label={t('users.role')}
        >
          <select
            id="roleId"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            <option value="">{t('users.selectRole')}</option>
            {roles.map(role => (
              <option key={role._id} value={role._id}>{role.name}</option>
            ))}
          </select>
        </FormField>
      )}
      {isEditMode && assignedMembers.length > 0 && (
        <div className="form-group">
          <label>{t('users.assignedMembers')}</label>
          <ul className="assigned-members-list">
            {assignedMembers.map(member => (
              <li key={member._id}>
                <Link to={`/admin/members/${member._id}/edit`}>{member.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      {isEditMode && <fieldset disabled={loading}><legend>{t('staffProfile.title')}</legend><StaffProfileFields value={staffProfile} onChange={setStaffProfile} onBusy={setPhotoBusy} disabled={loading} /></fieldset>}
      <Button type="submit" fullWidth loading={loading} disabled={photoBusy}>
        {loading ? t('common.saving') : submitButtonText}
      </Button>
    </form>
  );
};

export default UserForm;
